import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Spin, Switch, message, Select, Collapse } from 'antd';
import { RollbackOutlined, SendOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiService } from '../WorkflowDesigner/services/apiService';

const { TextArea } = Input;

const markdownChatStyles = {
  margin: 0,
  lineHeight: 1.5
};

const markdownComponents = (isUser) => ({
  p: ({ children }) => <p style={{ margin: '0 0 6px 0' }}>{children}</p>,
  h1: ({ children }) => <div style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px 0' }}>{children}</div>,
  h2: ({ children }) => <div style={{ fontSize: 14, fontWeight: 700, margin: '0 0 6px 0' }}>{children}</div>,
  h3: ({ children }) => <div style={{ fontSize: 13, fontWeight: 700, margin: '0 0 4px 0' }}>{children}</div>,
  ul: ({ children }) => <ul style={{ margin: '0 0 6px 0', paddingLeft: 18 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '0 0 6px 0', paddingLeft: 18 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
  code: ({ inline, children }) =>
    inline ? (
      <code style={{ background: isUser ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.08)', padding: '1px 4px', borderRadius: 4, fontSize: '0.92em' }}>{children}</code>
    ) : (
      <code style={{ display: 'block', background: isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)', padding: '6px 8px', borderRadius: 4, fontSize: 12, overflow: 'auto', marginTop: 4 }}>{children}</code>
    ),
  strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>
});

const buildChatHistoryForApi = (msgs) =>
  (msgs || [])
    .map((m) => ({
      role: (m.role || m.Role || 'user').toLowerCase(),
      content: (m.content || m.Content || '').trim()
    }))
    .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content);

const MetaFlowBuilderAIChat = ({
  eFormDefinitionId,
  currentFlowJson,
  onSuccess,
  isGenerating,
  setIsGenerating
}) => {
  const { t } = useLanguage();
  const messagesEndRef = useRef(null);
  const prevEFormDefinitionIdRef = useRef(eFormDefinitionId);
  const messagesRef = useRef([]);
  const [messages, setMessages] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  messagesRef.current = messages;
  const [aiPrompt, setAiPrompt] = useState('');
  const [includeCurrentFlow, setIncludeCurrentFlow] = useState(() => (currentFlowJson || '').trim().length > 0);
  const [aiProviders, setAiProviders] = useState([]);
  const [selectedProviderKey, setSelectedProviderKey] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [sendDefaultSystemPrompt, setSendDefaultSystemPrompt] = useState(true);
  const [modifySystemPrompt, setModifySystemPrompt] = useState(false);
  const [customSystemPrompt, setCustomSystemPrompt] = useState('');
  const [loadingDefaultPrompt, setLoadingDefaultPrompt] = useState(false);

  useEffect(() => {
    loadAiProviders();
  }, []);

  useEffect(() => {
    const hasExistingFlow = (currentFlowJson || '').trim().length > 0;
    setSendDefaultSystemPrompt(!hasExistingFlow);
  }, [currentFlowJson]);

  useEffect(() => {
    if (eFormDefinitionId) {
      const prevId = prevEFormDefinitionIdRef.current;
      prevEFormDefinitionIdRef.current = eFormDefinitionId;
      const currentMessages = messagesRef.current;
      const pending = currentMessages.filter(m => !m.Id && (m.role || m.Role));
      if (pending.length > 0 && !prevId) {
        const token = localStorage.getItem('token');
        const items = pending.map((m, i) => ({
          EFormDefinitionId: eFormDefinitionId,
          Role: m.role || m.Role,
          Content: m.content || m.Content || '',
          AttachmentInfo: m.attachmentInfo ?? m.AttachmentInfo ?? null,
          ResultFormName: m.resultFormName ?? m.ResultFormName ?? null,
          HtmlContent: m.htmlContent ?? m.HtmlContent ?? null,
          MessageIndex: i
        }));
        fetch('/api/FormsUpload/editing-ai-chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(items)
        })
          .then(res => {
            if (res.ok) loadChatHistory();
          })
          .catch(() => {})
          .finally(() => {});
        return;
      }
      loadChatHistory();
    } else {
      prevEFormDefinitionIdRef.current = null;
      setMessages([]);
    }
  }, [eFormDefinitionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const hasContent = (currentFlowJson || '').trim().length > 0;
    if (hasContent) {
      setIncludeCurrentFlow(true);
    }
  }, [currentFlowJson]);

  const loadAiProviders = async () => {
    try {
      setLoadingProviders(true);
      const providers = await apiService.fetchAiProviders();
      setAiProviders(providers || []);
      setSelectedProviderKey(prev => {
        if (prev) return prev;
        if (providers?.length > 0) {
          const active = providers.find(p => p.active);
          return (active || providers[0]).providerKey;
        }
        return '';
      });
    } catch (error) {
      console.error('Failed to load AI providers', error);
      message.error(t('eformDesigner.loadAiProvidersFailed'));
    } finally {
      setLoadingProviders(false);
    }
  };

  const loadChatHistory = async () => {
    if (!eFormDefinitionId) return;
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/FormsUpload/editing-ai-chat?eFormDefinitionId=${eFormDefinitionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Failed to load chat history');
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('Load chat history failed', e);
      setMessages([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadDefaultSystemPrompt = async (force = false) => {
    if (!force && customSystemPrompt.trim()) return;
    setLoadingDefaultPrompt(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/FormsUpload/meta-flow-ai-default-system-prompt', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await res.json();
      if (data.defaultSystemPrompt != null) setCustomSystemPrompt(data.defaultSystemPrompt);
    } catch (e) {
      message.error(t('eformDesigner.loadDefaultPromptFailed') || '無法載入預設系統 prompt');
    } finally {
      setLoadingDefaultPrompt(false);
    }
  };

  const handleModifySystemPromptChange = (checked) => {
    setModifySystemPrompt(checked);
    if (checked) loadDefaultSystemPrompt();
    else setCustomSystemPrompt('');
  };

  const handleSendDefaultSystemPromptChange = (checked) => {
    setSendDefaultSystemPrompt(checked);
    if (!checked) {
      setModifySystemPrompt(false);
      setCustomSystemPrompt('');
    }
  };

  const saveChatMessages = async (userItem, assistantItem) => {
    const token = localStorage.getItem('token');
    const res = await fetch('/api/FormsUpload/editing-ai-chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify([userItem, assistantItem])
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const errMsg = err.error || err.message || `HTTP ${res.status}`;
      message.error(t('eformDesigner.chatSaveFailed') || '對話紀錄儲存失敗：' + errMsg);
      throw new Error(errMsg);
    }
    return res.json();
  };

  const handleSend = async () => {
    if (!aiPrompt.trim()) {
      message.warning(t('metaFlowBuilder.aiAssistant.pleaseEnterRequirements') || '請輸入需求描述');
      return;
    }
    if (aiProviders.length > 0 && !selectedProviderKey) {
      message.warning(t('eformDesigner.aiProviderRequired'));
      return;
    }

    setIsGenerating(true);
    const nextIndex = messages.length;
    const chatHistory = buildChatHistoryForApi(messages);

    try {
      const token = localStorage.getItem('token');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15 * 60 * 1000);

      const requestData = {
        prompt: aiPrompt.trim(),
        includeCurrentFlow,
        providerKey: selectedProviderKey || null,
        useDefaultSystemPrompt: sendDefaultSystemPrompt,
        customSystemPrompt: sendDefaultSystemPrompt && modifySystemPrompt && customSystemPrompt.trim()
          ? customSystemPrompt.trim()
          : null,
        chatHistory: chatHistory.length > 0 ? chatHistory : undefined
      };
      if (includeCurrentFlow && (currentFlowJson || '').trim()) {
        requestData.currentFlowJson = (currentFlowJson || '').trim();
      }

      const response = await fetch('/api/FormsUpload/meta-flow-ai-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(requestData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const result = await response.json();

      if (!result.success) {
        message.error(`❌ ${t('metaFlowBuilder.aiAssistant.generationFailed') || '生成失敗'}${result.error || ''}`);
        return;
      }

      const finalFlowJson = result.flowJson;
      if (!finalFlowJson) {
        message.error(`❌ ${t('metaFlowBuilder.aiAssistant.generationFailed') || '生成失敗'}AI 返回了無效格式`);
        return;
      }

      message.success(t('metaFlowBuilder.aiAssistant.generationSuccess') || 'AI 已成功生成 Meta Flow 表單');
      onSuccess(finalFlowJson, result.flowName || null);

      const userContent = aiPrompt.trim();
      const assistantContent = (result.summary && result.summary.trim())
        ? result.summary.trim()
        : (t('metaFlowBuilder.aiAssistant.updatedSuccess') || '已根據您的要求更新 Meta Flow 表單');

      setAiPrompt('');

      const userItem = {
        EFormDefinitionId: eFormDefinitionId || '00000000-0000-0000-0000-000000000000',
        Role: 'user',
        Content: userContent,
        AttachmentInfo: null,
        ResultFormName: null,
        HtmlContent: null,
        MessageIndex: nextIndex
      };
      const assistantItem = {
        EFormDefinitionId: eFormDefinitionId || '00000000-0000-0000-0000-000000000000',
        Role: 'assistant',
        Content: assistantContent,
        AttachmentInfo: null,
        ResultFormName: result.flowName || null,
        HtmlContent: typeof finalFlowJson === 'string' ? finalFlowJson : JSON.stringify(finalFlowJson),
        MessageIndex: nextIndex + 1
      };

      if (eFormDefinitionId) {
        try {
          await saveChatMessages(userItem, assistantItem);
        } catch (e) {
          console.warn('Save chat failed', e);
        }
      }

      setMessages(prev => [
        ...prev,
        { ...userItem, Id: null, CreatedAt: new Date().toISOString() },
        { ...assistantItem, Id: null, HtmlContent: assistantItem.HtmlContent, CreatedAt: new Date().toISOString() }
      ]);
    } catch (error) {
      console.error('Meta Flow AI generate error', error);
      if (error.name === 'AbortError') {
        message.error(t('eformDesigner.requestTimeoutAiGeneration'));
      } else {
        message.error(`${t('metaFlowBuilder.aiAssistant.generationFailed') || '生成失敗'}${error.message}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUndoToVersion = (msg) => {
    const flowJson = msg.htmlContent || msg.HtmlContent;
    const name = msg.resultFormName || msg.ResultFormName;
    if (flowJson && typeof onSuccess === 'function') {
      onSuccess(flowJson, name || null);
      message.success(t('eformDesigner.restoredToThisVersion') || '已還原到此版本');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 280, height: '100%' }}>
      <div
        style={{
          flex: '1 1 0%',
          minHeight: 100,
          maxHeight: '40vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          border: '1px solid #e8e8e8',
          borderRadius: 8,
          padding: 8,
          marginBottom: 8,
          backgroundColor: '#fafafa'
        }}
      >
        {loadingHistory && (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Spin size="small" />
          </div>
        )}
        {!loadingHistory && messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#999', fontSize: 12, padding: 16 }}>
            {t('metaFlowBuilder.aiAssistant.chatEmptyHint') || '輸入需求，AI 將協助生成或修改 Meta Flow 表單'}
          </div>
        )}
        {!loadingHistory && messages.map((msg) => {
          const role = msg.role || msg.Role;
          const content = msg.content || msg.Content;
          const hasFlowJson = !!(msg.htmlContent || msg.HtmlContent);
          return (
            <div
              key={msg.id || `${msg.MessageIndex}-${role}`}
              style={{
                display: 'flex',
                justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: 8
              }}
            >
              <div
                style={{
                  maxWidth: '92%',
                  padding: '8px 12px',
                  borderRadius: 12,
                  backgroundColor: role === 'user' ? '#722ed1' : '#f0f0f0',
                  color: role === 'user' ? '#fff' : '#333',
                  fontSize: 13
                }}
              >
                {role === 'assistant' ? (
                  <div className="metaflow-ai-chat-markdown" style={markdownChatStyles}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents(false)}>
                      {content || ''}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{content}</div>
                )}
                {role === 'assistant' && hasFlowJson && (
                  <Button
                    type="link"
                    size="small"
                    icon={<RollbackOutlined />}
                    onClick={() => handleUndoToVersion(msg)}
                    style={{ padding: 0, marginTop: 6, color: role === 'user' ? '#fff' : '#722ed1' }}
                  >
                    {t('eformDesigner.restoreToThisVersion') || '還原到此版本'}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {!eFormDefinitionId && (
        <div style={{ fontSize: 11, color: '#999', marginBottom: 6 }}>
          {t('eformDesigner.chatSaveHintNewForm') || '儲存表單後，對話紀錄將寫入'}
        </div>
      )}

      <div style={{ flexShrink: 0 }}>
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <TextArea
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder={t('metaFlowBuilder.aiAssistant.placeholderExample') || '例如：建立包含姓名、電話、日期的客戶登記表單'}
            rows={3}
            style={{ paddingRight: 40, fontSize: 13 }}
            disabled={isGenerating}
          />
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={isGenerating}
            disabled={!aiPrompt.trim() || (aiProviders.length > 0 && !selectedProviderKey)}
            style={{
              position: 'absolute',
              right: 6,
              bottom: 6,
              minWidth: 32,
              height: 28,
              padding: '0 8px',
              backgroundColor: '#722ed1',
              borderColor: '#722ed1'
            }}
            title={t('metaFlowBuilder.aiAssistant.generate') || '生成表單'}
          />
        </div>

        {(currentFlowJson || '').trim() && (
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <Switch
              size="small"
              checked={includeCurrentFlow}
              onChange={setIncludeCurrentFlow}
              disabled={isGenerating}
              style={{ marginRight: 4 }}
            />
            {t('metaFlowBuilder.aiAssistant.includeCurrentFlow') || '包含當前 Meta Flow JSON（修改模式）'}
          </label>
        )}

        <Collapse
          size="small"
          items={[
            {
              key: '1',
              label: t('eformDesigner.advancedOptions') || '進階選項',
              children: (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: 12 }}>{t('eformDesigner.aiProvider')}</label>
                    <Select
                      value={selectedProviderKey || undefined}
                      onChange={setSelectedProviderKey}
                      placeholder={t('eformDesigner.aiProviderPlaceholder')}
                      style={{ width: '100%' }}
                      size="small"
                      loading={loadingProviders}
                      allowClear
                      options={aiProviders.map(p => ({
                        value: p.providerKey,
                        label: `${p.displayName}${p.active ? '' : ` (${t('workflowDesigner.aiProviderInactive')})`}`,
                        disabled: p.active === false
                      }))}
                    />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
                      <Switch size="small" checked={sendDefaultSystemPrompt} onChange={handleSendDefaultSystemPromptChange} style={{ marginRight: 8 }} disabled={isGenerating} />
                      {t('eformDesigner.sendDefaultSystemPrompt')}
                    </label>
                    {sendDefaultSystemPrompt && (
                      <label style={{ display: 'flex', alignItems: 'center', marginTop: 4, fontSize: 12, opacity: 0.9 }}>
                        <input
                          type="checkbox"
                          checked={modifySystemPrompt}
                          onChange={(e) => handleModifySystemPromptChange(e.target.checked)}
                          style={{ marginRight: 8 }}
                          disabled={isGenerating}
                        />
                        {t('eformDesigner.modifySystemPrompt')}
                      </label>
                    )}
                    {sendDefaultSystemPrompt && modifySystemPrompt && (
                      <TextArea
                        value={customSystemPrompt}
                        onChange={(e) => setCustomSystemPrompt(e.target.value)}
                        placeholder={loadingDefaultPrompt ? (t('eformDesigner.loadingDefaultPrompt') || '載入預設中...') : (t('eformDesigner.systemPromptPlaceholder') || '系統 prompt')}
                        rows={4}
                        style={{ fontSize: 12, fontFamily: 'monospace', marginTop: 6 }}
                        disabled={isGenerating || loadingDefaultPrompt}
                      />
                    )}
                  </div>
                </>
              )
            }
          ]}
        />
      </div>
    </div>
  );
};

export default MetaFlowBuilderAIChat;
